import { useState, useEffect } from "react";
import { MailCheck, Mail, Phone, Bell, Save, Plus, X, Send, Loader2, Clock, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Días de la semana
const DAYS_OF_WEEK = [
  { key: 0, label: 'D', fullLabel: 'Dom' },
  { key: 1, label: 'L', fullLabel: 'Lun' },
  { key: 2, label: 'M', fullLabel: 'Mar' },
  { key: 3, label: 'X', fullLabel: 'Mié' },
  { key: 4, label: 'J', fullLabel: 'Jue' },
  { key: 5, label: 'V', fullLabel: 'Vie' },
  { key: 6, label: 'S', fullLabel: 'Sáb' },
];

const NotificationSettings = () => {
  const [settings, setSettings] = useState({
    email_enabled: false,
    email_addresses: [],
    sms_enabled: false,
    phone_numbers: [],
    alert_on_overflow: true,
    alert_on_critical: true,
    alert_on_warning: false,
    alert_on_low: true,
    // Daily report options
    daily_report_enabled: true,
    daily_include_levels: true,
    daily_include_alerts: true,
    daily_include_weather: false,
    daily_include_cameras: false,
    // Schedule options
    schedule_days: [1, 2, 3, 4, 5, 6, 0], // All days by default
    schedule_times: ["07:00", "20:00"], // Default morning and evening
  });
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [open, setOpen] = useState(false);
  
  // Report content options (for manual send)
  const [reportOptions, setReportOptions] = useState({
    include_levels: true,
    include_alerts: true,
    include_weather: false,
    include_cameras: false,
  });

  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/notifications`);
      setSettings(response.data);
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings/notifications`, settings);
      setOpen(false);
    } catch (err) {
      console.error("Error saving settings:", err);
    } finally {
      setSaving(false);
    }
  };

  const addEmail = () => {
    if (newEmail && newEmail.includes("@")) {
      setSettings({
        ...settings,
        email_addresses: [...settings.email_addresses, newEmail],
      });
      setNewEmail("");
    }
  };

  const removeEmail = (email) => {
    setSettings({
      ...settings,
      email_addresses: settings.email_addresses.filter((e) => e !== email),
    });
  };

  const addPhone = () => {
    if (newPhone && newPhone.length >= 9) {
      setSettings({
        ...settings,
        phone_numbers: [...settings.phone_numbers, newPhone],
      });
      setNewPhone("");
    }
  };

  const removePhone = (phone) => {
    setSettings({
      ...settings,
      phone_numbers: settings.phone_numbers.filter((p) => p !== phone),
    });
  };

  const sendReportNow = async () => {
    setSendingReport(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/email/test-report`,
        { 
          options: reportOptions 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Informe enviado correctamente");
    } catch (err) {
      console.error("Error sending report:", err);
      toast.error(err.response?.data?.detail || "Error al enviar el informe");
    } finally {
      setSendingReport(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 p-0 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
          data-testid="notification-settings-btn"
        >
          <MailCheck className="w-[44px] h-[44px]" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg bg-zinc-900 border-zinc-800 max-h-[85vh] overflow-y-auto">
        <DialogHeader className="sticky top-0 bg-zinc-900 z-10 pb-2">
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <Bell className="w-14 h-14" />
            Configuración de Notificaciones
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Email notifications */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-zinc-400" />
                <Label className="text-zinc-200">Notificaciones por Email</Label>
              </div>
              <Switch
                checked={settings.email_enabled}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, email_enabled: checked })
                }
              />
            </div>
            
            {settings.email_enabled && (
              <div className="space-y-2 pl-6">
                <div className="flex gap-2">
                  <Input
                    placeholder="email@ejemplo.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-zinc-100"
                    onKeyPress={(e) => e.key === "Enter" && addEmail()}
                  />
                  <Button size="sm" variant="outline" onClick={addEmail}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {settings.email_addresses.map((email) => (
                    <span
                      key={email}
                      className="bg-zinc-800 text-zinc-300 text-xs px-2 py-1 rounded flex items-center gap-1"
                    >
                      {email}
                      <X
                        className="w-3 h-3 cursor-pointer hover:text-red-400"
                        onClick={() => removeEmail(email)}
                      />
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Daily Report Configuration */}
          <div className="border-t border-zinc-800 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-zinc-200">Informe Automático Programado</Label>
                <p className="text-xs text-zinc-500 mt-1">Configura días y horarios de envío</p>
              </div>
              <Switch
                checked={settings.daily_report_enabled}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, daily_report_enabled: checked })
                }
                data-testid="daily-report-toggle"
              />
            </div>
            
            {settings.daily_report_enabled && (
              <div className="bg-zinc-800/50 rounded-lg p-3 space-y-4">
                {/* Schedule: Days of week */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-zinc-400" />
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Días de envío</p>
                  </div>
                  <div className="flex gap-1">
                    {DAYS_OF_WEEK.map(day => (
                      <button
                        key={day.key}
                        type="button"
                        onClick={() => {
                          const currentDays = settings.schedule_days || [];
                          const newDays = currentDays.includes(day.key)
                            ? currentDays.filter(d => d !== day.key)
                            : [...currentDays, day.key].sort((a,b) => a - b);
                          setSettings({...settings, schedule_days: newDays});
                        }}
                        className={`w-8 h-8 rounded text-xs font-semibold transition-colors ${
                          (settings.schedule_days || []).includes(day.key)
                            ? 'bg-blue-600 text-white'
                            : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                        }`}
                        title={day.fullLabel}
                        data-testid={`day-toggle-${day.key}`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Schedule: Times */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-zinc-400" />
                    <p className="text-xs text-zinc-400 uppercase tracking-wider">Horarios de envío</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(settings.schedule_times || []).map((time, idx) => (
                      <div key={idx} className="flex items-center gap-1 bg-zinc-700 rounded px-2 py-1">
                        <input
                          type="time"
                          value={time}
                          onChange={(e) => {
                            const newTimes = [...(settings.schedule_times || [])];
                            newTimes[idx] = e.target.value;
                            setSettings({...settings, schedule_times: newTimes});
                          }}
                          className="bg-transparent text-zinc-200 text-sm border-none outline-none w-20"
                          data-testid={`time-input-${idx}`}
                        />
                        {(settings.schedule_times || []).length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newTimes = (settings.schedule_times || []).filter((_, i) => i !== idx);
                              setSettings({...settings, schedule_times: newTimes});
                            }}
                            className="text-zinc-400 hover:text-red-400"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const currentTimes = settings.schedule_times || [];
                        if (currentTimes.length < 6) {
                          setSettings({...settings, schedule_times: [...currentTimes, "12:00"]});
                        }
                      }}
                      className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-xs text-zinc-300"
                      data-testid="add-time-btn"
                    >
                      <Plus className="w-3 h-3 inline mr-1" />
                      Añadir hora
                    </button>
                  </div>
                </div>

                {/* Content options */}
                <div className="space-y-2 pt-2 border-t border-zinc-700">
                  <p className="text-xs text-zinc-400 uppercase tracking-wider">Contenido del informe</p>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.daily_include_levels}
                        onChange={(e) => setSettings({...settings, daily_include_levels: e.target.checked})}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-green-500"
                        data-testid="daily-include-levels"
                      />
                      <span className="text-sm text-zinc-300">Niveles actuales</span>
                    </label>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.daily_include_alerts}
                        onChange={(e) => setSettings({...settings, daily_include_alerts: e.target.checked})}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-green-500"
                        data-testid="daily-include-alerts"
                      />
                      <span className="text-sm text-zinc-300">Estado alarmas</span>
                    </label>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.daily_include_weather}
                        onChange={(e) => setSettings({...settings, daily_include_weather: e.target.checked})}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-green-500"
                        data-testid="daily-include-weather"
                      />
                      <span className="text-sm text-zinc-300">Previsión tiempo</span>
                    </label>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.daily_include_cameras}
                        onChange={(e) => setSettings({...settings, daily_include_cameras: e.target.checked})}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-green-500"
                        data-testid="daily-include-cameras"
                      />
                      <span className="text-sm text-zinc-300">Estado cámaras</span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Send report now button */}
          <div className="border-t border-zinc-800 pt-4">
            <div className="space-y-3">
              <div>
                <Label className="text-zinc-200">Enviar Informe Ahora</Label>
                <p className="text-xs text-zinc-500 mt-1">Selecciona qué incluir en el email</p>
              </div>
              
              {/* Report content options */}
              <div className="grid grid-cols-2 gap-2 pl-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reportOptions.include_levels}
                    onChange={(e) => setReportOptions({...reportOptions, include_levels: e.target.checked})}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-500"
                  />
                  <span className="text-sm text-zinc-300">Niveles actuales</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reportOptions.include_alerts}
                    onChange={(e) => setReportOptions({...reportOptions, include_alerts: e.target.checked})}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-500"
                  />
                  <span className="text-sm text-zinc-300">Estado alarmas</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reportOptions.include_weather}
                    onChange={(e) => setReportOptions({...reportOptions, include_weather: e.target.checked})}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-500"
                  />
                  <span className="text-sm text-zinc-300">Previsión tiempo</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reportOptions.include_cameras}
                    onChange={(e) => setReportOptions({...reportOptions, include_cameras: e.target.checked})}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-500"
                  />
                  <span className="text-sm text-zinc-300">Estado cámaras</span>
                </label>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={sendReportNow}
                disabled={sendingReport || (!reportOptions.include_levels && !reportOptions.include_alerts && !reportOptions.include_weather && !reportOptions.include_cameras)}
                className="w-full bg-blue-600/20 border-blue-500/30 text-blue-400 hover:bg-blue-600/30"
              >
                {sendingReport ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {sendingReport ? "Enviando..." : "Enviar Informe"}
              </Button>
            </div>
          </div>

          {/* SMS notifications */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-zinc-400" />
                <Label className="text-zinc-200">Notificaciones SMS</Label>
              </div>
              <Switch
                checked={settings.sms_enabled}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, sms_enabled: checked })
                }
              />
            </div>
            
            {settings.sms_enabled && (
              <div className="space-y-2 pl-6">
                <div className="flex gap-2">
                  <Input
                    placeholder="+34 600 000 000"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-zinc-100"
                    onKeyPress={(e) => e.key === "Enter" && addPhone()}
                  />
                  <Button size="sm" variant="outline" onClick={addPhone}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {settings.phone_numbers.map((phone) => (
                    <span
                      key={phone}
                      className="bg-zinc-800 text-zinc-300 text-xs px-2 py-1 rounded flex items-center gap-1"
                    >
                      {phone}
                      <X
                        className="w-3 h-3 cursor-pointer hover:text-red-400"
                        onClick={() => removePhone(phone)}
                      />
                    </span>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-500">
                  Nota: SMS requiere configuración de proveedor (Twilio, etc.)
                </p>
              </div>
            )}
          </div>

          {/* Alert types */}
          <div className="space-y-3 pt-4 border-t border-zinc-800">
            <Label className="text-zinc-200">Tipos de alerta a notificar:</Label>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between bg-red-500/10 p-2 rounded">
                <span className="text-xs text-red-400">DESBORDE</span>
                <Switch
                  checked={settings.alert_on_overflow}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, alert_on_overflow: checked })
                  }
                />
              </div>
              <div className="flex items-center justify-between bg-red-500/5 p-2 rounded">
                <span className="text-xs text-red-300">CRÍTICO</span>
                <Switch
                  checked={settings.alert_on_critical}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, alert_on_critical: checked })
                  }
                />
              </div>
              <div className="flex items-center justify-between bg-amber-500/10 p-2 rounded">
                <span className="text-xs text-amber-400">ALERTA</span>
                <Switch
                  checked={settings.alert_on_warning}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, alert_on_warning: checked })
                  }
                />
              </div>
              <div className="flex items-center justify-between bg-blue-500/10 p-2 rounded">
                <span className="text-xs text-blue-400">BAJO</span>
                <Switch
                  checked={settings.alert_on_low}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, alert_on_low: checked })
                  }
                />
              </div>
            </div>
          </div>

          {/* Save button */}
          <Button
            className="w-full bg-blue-600 hover:bg-blue-700"
            onClick={saveSettings}
            disabled={saving}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Guardando..." : "Guardar configuración"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationSettings;
